import { DateTime } from 'luxon';

/**
 * Fechas y zonas horarias del portal.
 *
 * Todo lo que llega de la API son instantes en UTC. La capa de presentación los
 * convierte a la zona que corresponda; el portal de clientes fija Madrid como
 * referencia y lo avisa expresamente durante la reserva.
 */

/**
 * Cómo se llama esa zona para una persona: `Europe/Madrid` → `Madrid (UTC+02:00)`.
 *
 * Se enseña el **desfase** y no la abreviatura del huso —`CEST`, `WEST`—
 * porque esa abreviatura depende de los datos de localización que traiga cada
 * navegador: en unos sale `CEST` y en otros `GMT+2`. El desfase es inequívoco y
 * sale igual en todas partes, que es justo lo que se necesita para evitar
 * malentendidos sobre a qué hora es la llamada.
 */
export function nombreDeZona(zona: string, referencia: Date = new Date()): string {
  const momento = DateTime.fromJSDate(referencia, { zone: zona });
  const ciudad = (zona.split('/').at(-1) ?? zona).replace(/_/g, ' ');
  return `${ciudad} (UTC${momento.toFormat('ZZ')})`;
}

/** `2026-09-08` → `martes, 8 de septiembre`. */
export function fechaLarga(fechaIso: string, zona: string): string {
  const dia = DateTime.fromISO(fechaIso, { zone: zona });
  return dia.setLocale('es').toFormat("cccc, d 'de' LLLL");
}

/** `2026-09-08` → `mar 8 sep`. */
export function fechaCorta(fechaIso: string, zona: string): string {
  const dia = DateTime.fromISO(fechaIso, { zone: zona });
  return dia.setLocale('es').toFormat('ccc d LLL');
}

/** Partes breves para una tarjeta de calendario: `mar`, `8`, `sept`. */
export function fechaParaCalendario(
  fechaIso: string,
  zona: string,
): { semana: string; dia: string; mes: string } {
  const fecha = DateTime.fromISO(fechaIso, { zone: zona }).setLocale('es');
  return {
    semana: fecha.toFormat('ccc').replace('.', ''),
    dia: fecha.toFormat('d'),
    mes: fecha.toFormat('LLL').replace('.', ''),
  };
}

/** Un instante en ISO → `martes, 8 de septiembre a las 09:15`. */
export function instanteLargo(instanteIso: string, zona: string): string {
  const momento = DateTime.fromISO(instanteIso, { zone: zona });
  return momento.setLocale('es').toFormat("cccc, d 'de' LLLL 'a las' HH:mm");
}

/** Solo la hora: `09:15`. */
export function soloHora(instanteIso: string, zona: string): string {
  return DateTime.fromISO(instanteIso, { zone: zona }).toFormat('HH:mm');
}

/**
 * Junta un día y una hora locales en el instante absoluto que le corresponde.
 *
 * Es lo que se manda a `POST /citas`: un ISO con desfase, para que no haya
 * ninguna ambigüedad sobre qué instante se ha elegido.
 */
export function comoInstante(fechaIso: string, hora: string, zona: string): string {
  return DateTime.fromISO(`${fechaIso}T${hora}`, { zone: zona }).toISO() ?? '';
}

/** El rango de fechas que hay que pedirle a la API para una semana concreta. */
export interface RangoDeDias {
  desde: string;
  hasta: string;
}

/**
 * La ventana de días que se enseña de una vez.
 *
 * Empieza hoy y no antes: no tiene sentido enseñar días pasados en un
 * calendario de reservas.
 */
export function ventanaDeDias(
  desplazamientoEnSemanas: number,
  zona: string,
  dias = 7,
  hoy: Date = new Date(),
): RangoDeDias {
  const primerDiaPosible = DateTime.fromJSDate(hoy, { zone: zona }).startOf('day');
  const inicio = primerDiaPosible.plus({ weeks: desplazamientoEnSemanas });
  const desde = inicio < primerDiaPosible ? primerDiaPosible : inicio;
  return {
    desde: desde.toISODate() ?? '',
    hasta: desde.plus({ days: dias - 1 }).toISODate() ?? '',
  };
}
